// @ts-nocheck
/**
 * Semantic color palette for the mapping agent's source-side sections —
 * the buckets the agent detects in the *input* sheet (Revenue, COGS, OpEx,
 * Labor, Other, BelowLine). Reused across SheetStructureCard,
 * ClassificationsTable, and any future tool widget that needs to signal
 * section identity. Keeping it in one place means a color edit propagates
 * everywhere the section is rendered.
 *
 * Distinct from TenantContext.getCategoryColor, which maps the
 * *destination* chart-of-accounts categories ("Revenue", "Cost of Sales",
 * "Operating Expenses", ...). Those are user-facing tenant categories;
 * these are the agent's internal sections.
 */

export type SectionKey =
  | 'Revenue'
  | 'COGS'
  | 'OpEx'
  | 'Labor'
  | 'Other'
  | 'BelowLine'
  | 'Unknown';

interface SectionStyle {
  fg: string;     // strong text/icon color
  bg: string;     // tinted background (rgba)
  border: string; // matching border (rgba)
  dot: string;    // solid dot color
}

const PALETTE: Record<SectionKey, SectionStyle> = {
  Revenue:   { fg: '#047857', bg: 'rgba(16, 185, 129, 0.10)', border: 'rgba(16, 185, 129, 0.32)', dot: '#10b981' },
  COGS:      { fg: '#b45309', bg: 'rgba(245, 158, 11, 0.10)', border: 'rgba(245, 158, 11, 0.32)', dot: '#f59e0b' },
  OpEx:      { fg: '#4338ca', bg: 'rgba(99, 102, 241, 0.10)', border: 'rgba(99, 102, 241, 0.32)', dot: '#6366f1' },
  Labor:     { fg: '#6d28d9', bg: 'rgba(139, 92, 246, 0.10)', border: 'rgba(139, 92, 246, 0.32)', dot: '#8b5cf6' },
  Other:     { fg: '#475569', bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.32)', dot: '#64748b' },
  BelowLine: { fg: '#be123c', bg: 'rgba(244, 63, 94, 0.10)',  border: 'rgba(244, 63, 94, 0.32)',  dot: '#f43f5e' },
  Unknown:   { fg: '#475569', bg: 'rgba(100, 116, 139, 0.08)', border: 'rgba(100, 116, 139, 0.24)', dot: '#94a3b8' },
};

export function getSectionStyle(name: string | undefined | null): SectionStyle {
  if (!name) return PALETTE.Unknown;
  const key = name.trim() as SectionKey;
  return PALETTE[key] ?? PALETTE.Unknown;
}

// Group a sequence of sections by name, preserving order of first
// occurrence. Used to collapse repeated "Revenue ×3" rows in the
// inspect-sheet widget so the user sees the gestalt instead of a long
// table.
export function groupSections<T extends { name: string; start_row: number; end_row: number }>(
  sections: T[]
): Array<{ name: string; blocks: number; rows: number; ranges: T[] }> {
  const out: Record<string, { name: string; blocks: number; rows: number; ranges: T[] }> = {};
  const order: string[] = [];
  for (const s of sections) {
    const key = s.name;
    if (!out[key]) {
      out[key] = { name: key, blocks: 0, rows: 0, ranges: [] };
      order.push(key);
    }
    out[key].blocks += 1;
    out[key].rows += Math.max(0, s.end_row - s.start_row + 1);
    out[key].ranges.push(s);
  }
  return order.map((k) => out[k]);
}
